import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import Button from "./Button";

interface HeaderProps {
  className?: string;
}

const Header = ({ className = "" }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { label: "Home", path: "/homepage", icon: "Home" },
    { label: "Projects", path: "/projects", icon: "FolderGit2" },
    { label: "Skills", path: "/skills", icon: "Code2" },
    { label: "Collaboration", path: "/collaboration", icon: "Users" },
    { label: "Contact", path: "/contact", icon: "Mail" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActivePath = (path: string) => location.pathname === path;

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/qr/MIPMGLGF4RZ5O1", "_blank");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-elevation-2"
          : "bg-transparent"
        } ${className}`}
    >
      <div className="flex items-center justify-between h-16 px-4 lg:px-8">
        <Link
          to="/homepage"
          className="flex items-center gap-3 group transition-transform duration-300 hover:scale-105"
        >
          <div className="relative">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-card">
              <span className="text-primary-foreground font-mono font-bold text-lg">
                MFA
              </span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-accent rounded-full border-2 border-background animate-pulse-slow"></div>
          </div>
          <div className="hidden sm:block">
            <div className="font-bold text-foreground text-lg tracking-tight">
              Muhammad Fotih
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              &lt;Developer /&gt;
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative px-4 py-2 rounded-md font-medium text-sm transition-all duration-300 ${isActivePath(item.path)
                  ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
            >
              <span className="flex items-center gap-2">
                <Icon name={item.icon} size={16} />
                {item.label}
              </span>
              {isActivePath(item.path) && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></span>
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            variant="default"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={handleWhatsAppClick}
            className="hidden md:flex bg-primary hover:bg-primary/90"
          >
            Let's Collaborate
          </Button>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors duration-300"
            aria-label="Toggle menu"
          >
            <Icon
              name={isMobileMenuOpen ? "X" : "Menu"}
              size={24}
              color="var(--color-foreground)"
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-surface/98 backdrop-blur-md border-t border-border shadow-elevation-3">
          <nav className="flex flex-col p-4 gap-2">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-md font-medium text-sm transition-all duration-300 ${isActivePath(item.path)
                    ? "text-primary bg-primary/10 border-l-2 border-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
              >
                <Icon name={item.icon} size={18} />
                {item.label}
              </Link>
            ))}
            <Button
              variant="default"
              size="default"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={handleWhatsAppClick}
              className="mt-4 w-full bg-primary hover:bg-primary/90"
            >
              Let's Collaborate
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;